/**
 * Composable for managing report data with filtering, sorting, and pagination
 */
import { computed, reactive, ref, watch } from "vue";
import { extractChainedFilterOptions, hasActiveFilters as checkActiveFilters, filterData, sortData } from "./utils";
export function useReportDataset(config) {
    // Filters state
    const filters = reactive({});
    config.filterFields.forEach((key) => {
        filters[key] = [];
    });
    // Sorts state
    const sorts = reactive(config.defaultSorts || []);
    // Pagination state
    const currentPage = ref(1);
    const itemsPerPage = ref(config.itemsPerPage || 10);
    // Computed: Source data from config
    const sourceData = computed(() => config.tableData());
    // Computed: Table fields from config
    const tableFields = computed(() => config.tableFields());
    // Computed: Filter options
    const filterOptions = computed(() => {
        return extractChainedFilterOptions(sourceData.value, config.filterFields, filters);
    });
    // Computed: Filtered data
    const filteredData = computed(() => {
        return filterData(sourceData.value, filters);
    });
    // Computed: Sorted data
    const sortedData = computed(() => {
        return sortData(filteredData.value, sorts);
    });
    // Computed: Pagination
    const totalItems = computed(() => sortedData.value.length);
    const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));
    // Computed: Paginated data (final data)
    const paginatedData = computed(() => {
        const start = (currentPage.value - 1) * itemsPerPage.value;
        const end = start + itemsPerPage.value;
        return sortedData.value.slice(start, end);
    });
    // Computed: Has active filters
    const hasActiveFilters = computed(() => {
        return checkActiveFilters(filters);
    });
    watch(filterOptions, (nextFilterOptions) => {
        let hasRemovedInvalidValues = false;
        Object.keys(filters).forEach((filterKey) => {
            var _a, _b;
            const selectedValues = (_a = filters[filterKey]) !== null && _a !== void 0 ? _a : [];
            if (selectedValues.length === 0) {
                return;
            }
            const validValues = new Set(((_b = nextFilterOptions[filterKey]) !== null && _b !== void 0 ? _b : []).map((option) => option.value));
            const nextSelectedValues = selectedValues.filter((value) => validValues.has(value));
            if (nextSelectedValues.length !== selectedValues.length) {
                filters[filterKey] = nextSelectedValues;
                hasRemovedInvalidValues = true;
            }
        });
        if (hasRemovedInvalidValues) {
            currentPage.value = 1;
        }
    }, { immediate: true });
    // Methods: Sorting
    const toggleSort = (field) => {
        const existingSort = sorts.find((s) => s.field === field);
        sorts.splice(0, sorts.length);
        if (!existingSort) {
            sorts.push({ field, direction: "asc" });
        }
        else if (existingSort.direction === "asc") {
            sorts.push({ field, direction: "desc" });
        }
        currentPage.value = 1;
    };
    const setSorts = (newSorts) => {
        sorts.splice(0, sorts.length, ...newSorts);
        currentPage.value = 1;
    };
    const clearSorts = () => {
        sorts.splice(0, sorts.length);
        currentPage.value = 1;
    };
    const getSortState = (field) => {
        const sort = sorts.find((s) => s.field === field);
        return sort ? sort.direction : null;
    };
    // Methods: Filtering
    const setFilter = (filterKey, values) => {
        filters[filterKey] = values;
        currentPage.value = 1;
    };
    const clearFilter = (filterKey) => {
        filters[filterKey] = [];
        currentPage.value = 1;
    };
    const clearFilters = () => {
        Object.keys(filters).forEach((key) => {
            filters[key] = [];
        });
        currentPage.value = 1;
    };
    // Methods: Pagination
    const setPage = (page) => {
        if (page >= 1 && page <= totalPages.value) {
            currentPage.value = page;
        }
    };
    const setItemsPerPage = (items) => {
        itemsPerPage.value = items;
        currentPage.value = 1;
    };
    return {
        // Computed data
        filteredData,
        sortedData,
        data: paginatedData,
        // Table structure
        tableFields,
        // Filters
        filters,
        filterOptions,
        hasActiveFilters,
        setFilter,
        clearFilter,
        clearFilters,
        // Sorts
        sorts,
        toggleSort,
        setSorts,
        clearSorts,
        getSortState,
        // Pagination
        currentPage,
        itemsPerPage,
        totalItems,
        totalPages,
        setPage,
        setItemsPerPage,
    };
}
